import { Card, Typography } from "@codat/orchard-ui";
import { IconProps } from "@codat/orchard-ui/dist/es/icons/types";
import React from "react";
import { Link } from "react-router-dom-v5-compat";

import { Button, ButtonProps } from "components/Button/Button";

import SettingsNavItem, { SettingsNavItemProps } from "./SettingsNavItem";
import styles from "./SettingsSection.module.scss";

type HeaderButtonProps = Pick<ButtonProps, "buttonName" | "href" | "label"> & {
  icon: React.ComponentType<IconProps>;
};

type HeaderLinkProps = {
  name: string;
  icon: React.ComponentType<IconProps>;
  path: string;
  linkText: string;
};

interface SettingsSectionProps {
  title: string;
  headerButton?: HeaderButtonProps;
  headerLink?: HeaderLinkProps;
  icon: React.ComponentType<IconProps>;
  iconColor:
    | "action"
    | "positive"
    | "negative"
    | "warning"
    | "neutral"
    | "interface";
  navItems: SettingsNavItemProps[];
  ["data-testid"]?: string;
}

const SettingsSection = ({
  title,
  icon: Icon,
  iconColor,
  navItems = [],
  headerButton,
  headerLink,
  "data-testid": testId,
}: SettingsSectionProps) => {
  const showHeaderButton =
    !!headerButton && !!headerButton.buttonName && !!headerButton.icon;
  const showHeaderLink = !!headerLink && !!headerLink.path;
  return (
    <Card className={styles.settingsSection} data-testid={testId}>
      <div className={styles.header}>
        <div
          className={styles.iconBg}
          style={{
            backgroundColor: `var(--color-ui-${iconColor})`,
            border: `solid 1px var(--color-ui-${iconColor}-mid)`,
          }}
        >
          <Icon />
        </div>
        <Typography as="h2" variant="heading_md" className={styles.title}>
          {title}
        </Typography>
        {showHeaderButton && (
          <div className={styles.headerButton}>
            <Button
              buttonName={headerButton.buttonName}
              href={headerButton.href}
              target="_blank"
              rel="noopener noreferrer"
              color="interface"
              variant="secondary"
              size="small"
            >
              <span className={styles.btnChildren}>
                <headerButton.icon className={styles.btnIcon} />
                {headerButton.label}
              </span>
            </Button>
          </div>
        )}
        {showHeaderLink && (
          <div className={styles.headerButton}>
            <Link to={headerLink.path}>
              <Button
                buttonName={headerLink.name}
                to={headerLink.path}
                color="interface"
                variant="secondary"
                size="small"
              >
                <span className={styles.btnChildren}>
                  <headerLink.icon className={styles.btnIcon} />
                  {headerLink.linkText}
                </span>
              </Button>
            </Link>
          </div>
        )}
      </div>
      <ul className={styles.navItemsList}>
        {navItems.map((props) => (
          <SettingsNavItem {...props} key={`${props.name}-${props.path}`} />
        ))}
      </ul>
    </Card>
  );
};

export default SettingsSection;
